<template>
  <div id="q-app">
    <LoadingIndicator />

    <transition
      enter-active-class="animated fadeIn"
      leave-active-class="animated fadeOut"
      appear
      :duration="300"
    >
      <router-view v-if="!isBooting" />
    </transition>
  </div>
</template>
<script>
import {
  mapActions, mapGetters, mapMutations, mapState,
} from 'vuex';
import { useIdle } from '@vueuse/core';
import errorDialog from '@stefna/quasar-pilot-core/src/mixins/errorDialog';
import LoadingIndicator from '@stefna/quasar-pilot-core/src/components/ui/LoadingIndicator';
import Qr from '@stefna/quasar-pilot-core/src/mixins/Qr';
import constants from './lib/constants';

export default {
  name: 'App',
  components: { LoadingIndicator },
  mixins: [Qr, errorDialog],

  data() {
    return {
      waitUpdateInterval: null,
      idle: null,
    };
  },

  computed: {
    ...mapState('application', ['isBooting', 'isQR', 'isKiosk', 'language']),
    ...mapState('location', { location: 'current' }),
    ...mapGetters('location', ['findLocationsByDeliveryMethod']),
  },

  watch: {
    async idle(isIdle) {
      if (isIdle && this.isKiosk && this.$route.name !== 'Idle') {
        this.flushOrder();
        await this.$router.replace({ name: 'Idle' });
        window.location.reload(true);
      }
    },
    // eslint-disable-next-line func-names
    '$q.appVisible': async function (visible) {
      // Checks if app is visible or in background
      if (visible) {
        try {
          await this.fetchDeliveryMethods();
          await this.fetchAllPaymentMethods();
          await this.fetchLocations();
          await this.fetchCategories();
        }
        catch (e) {
          this.showErrorDialog(e);
        }
        finally {
          await this.initKiosk();
          await this.initQR();
          if (!this.isQR) {
            await this.initDefaultLocation(
              constants.DEFAULT_LOCATION,
              constants.DEFAULT_DELIVERY_METHOD,
            );
          }
          this.startWaitUpdates();
        }
      }
      else {
        this.stopWaitUpdates();
      }
    },

    language() {
      this.$i18n.locale = this.language;
    },
  },

  async mounted() {
    const { idle } = useIdle(4 * 60 * 1000); // 4 min
    this.idle = idle;

    try {
      await this.fetchLocations();
      await this.fetchDeliveryMethods();
      await this.fetchAllPaymentMethods();
      await this.fetchCategories();
    }
    catch (e) {
      this.showErrorDialog(e);
    }
    finally {
      await this.initKiosk();
      await this.initQR();
      if (!this.isQR) {
        await this.initDefaultLocation(
          constants.DEFAULT_LOCATION,
          constants.DEFAULT_DELIVERY_METHOD,
        );
      }
      this.startWaitUpdates();
      this.bootComplete();

      // Replace current route with the home route.
      // This should remove uuid's from url when scanning qr.
      if (this.isQR) {
        // await this.$router.replace({ name: 'Home' }).catch(() => {});
      }
    }
  },

  created() {
    window.addEventListener('beforeinstallprompt', (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
    });
  },

  methods: {
    ...mapActions('order', { flushOrder: 'flush' }),
    ...mapActions('category', ['fetchCategories']),
    ...mapActions('location', ['fetchLocations', 'fetchDeliveryMethods']),
    ...mapActions('payment', ['fetchAllPaymentMethods']),
    ...mapMutations('application', ['bootComplete']),
    ...mapMutations('user', { flushUser: 'flush' }),

    startWaitUpdates() {
      if (!this.waitUpdateInterval) {
        this.waitUpdateInterval = setInterval(() => {
          // Keep updating waiting times
          this.fetchLocations();
        }, 600000); // 10 minutes
      }
    },

    stopWaitUpdates() {
      if (this.waitUpdateInterval) {
        clearInterval(this.waitUpdateInterval);
        this.waitUpdateInterval = null;
      }
    },
  },
};
</script>
